<template>
  <section class="page-panel list-page">
    <div class="list-page__header grid grid-gap-16 grid-gap-sm-0 align-items-center">
      <h2 class="col-12 col-sm-6">{{ pageTitle }}</h2>

      <ToolbarActionButton
        class="col-12 col-sm-6 justify-self-sm-end"
        text="Unesi vozilo"
        :to="{ name: 'inventory-vehicle-create' }"
      />
    </div>

    <Filters
      v-model="state.vehicleFilters"
      :filter-fields="vehicleFilterFields"
      @change="loadVehiclesAsync(1)"
    />

    <v-data-table-server
      class="table list-table"
      :headers="vehicleTableHeaders"
      :items="state.vehicles"
      :items-length="state.totalResults"
      :items-per-page="state.itemsPerPage"
      :loading="state.isLoadingVehicles"
      :page="state.currentPage"
      item-value="id"
      loading-text="Učitavanje vozila..."
      no-data-text="Nema vozila"
      hide-default-footer
      @click:row="openVehicleDetail"
    >
      <template #item.brandCode="{ item }">
        {{ getEventBrandName(item.brandCode) }}
      </template>

      <template #item.status="{ item }">
        <div class="d-flex ga-1 justify-end">
          <span
            class="status-chip"
            :class="`status-chip--${getVehicleStatus(item)}`"
          >
            {{ vehicleStatusLabels[getVehicleStatus(item)] }}
          </span>
          <span
            v-if="item.isArchived"
            class="status-chip status-chip--archived"
          >
            Arhiviran
          </span>
        </div>
      </template>

      <template #item.actions="{ item }">
        <v-menu
          content-class="table-menu"
          location="bottom end"
        >
          <template #activator="{ props: menuProps }">
            <v-btn
              v-bind="menuProps"
              aria-label="Otvori akcije"
              class="button table__actions-button"
              :disabled="state.isSavingVehicleAction || state.isLoadingVehicles"
              icon="mdi-dots-horizontal"
              size="small"
              variant="text"
              @click.stop
            />
          </template>

          <div class="table-menu__content grid-stack grid-gap-16">
            <v-btn
              class="button button--menu"
              :to="createVehicleRoute('inventory-vehicle-detail', item)"
              variant="text"
            >
              Detalji vozila
            </v-btn>
            <v-btn
              v-if="!item.isArchived"
              class="button button--menu"
              :to="createVehicleRoute('inventory-vehicle-edit', item)"
              variant="text"
            >
              Uredi vozilo
            </v-btn>
            <v-btn
              v-if="!item.isArchived"
              class="button button--menu"
              :disabled="state.isSavingVehicleAction"
              variant="text"
              @click.stop="openVehicleActionDialog(item, 'archive')"
            >
              Arhiviraj vozilo
            </v-btn>
            <v-btn
              v-if="!item.isArchived"
              class="button button--menu button--text-danger table-menu__button--danger"
              :disabled="state.isSavingVehicleAction"
              variant="text"
              @click.stop="openVehicleActionDialog(item, 'delete')"
            >
              <v-icon
                class="table-menu__icon"
                icon="mdi-trash-can-outline"
                size="16"
              />
              Ukloni vozilo
            </v-btn>
            <v-btn
              v-if="item.isArchived"
              class="button button--menu"
              :disabled="state.isSavingVehicleAction"
              variant="text"
              @click.stop="setVehicleArchivedAsync(item, false)"
            >
              Odarhiviraj vozilo
            </v-btn>
          </div>
        </v-menu>
      </template>
    </v-data-table-server>

    <Pagination
      :page="state.currentPage"
      :per-page="state.itemsPerPage"
      :total-results="state.totalResults"
      @page-change="loadVehiclesAsync"
    />

    <AppConfirmDialog
      v-model="state.vehicleConfirmDialogOpen"
      :title="state.selectedVehicleAction === 'archive' ? 'Arhiviraj vozilo?' : 'Ukloni vozilo?'"
      :subtitle="selectedVehicleSubtitle"
      :loading="state.isSavingVehicleAction"
      :close-on-confirm="false"
      :message="state.selectedVehicleAction === 'archive'
        ? 'Jeste li sigurni da želite arhivirati ovo vozilo?'
        : 'Jeste li sigurni da želite ukloniti ovo vozilo?'"
      :primary-text="state.selectedVehicleAction === 'archive' ? 'Arhiviraj' : 'Ukloni'"
      secondary-text="Odustani"
      @close="clearSelectedVehicleForAction"
      @confirm="state.selectedVehicleAction === 'archive'
        ? setVehicleArchivedAsync(state.selectedVehicleForAction, true)
        : deleteSelectedVehicleAsync()"
    />

    <AppConfirmDialog
      v-model="state.occupiedVehicleDialogOpen"
      variant="blocked"
      :title="state.selectedVehicleAction === 'archive' ? 'Vozilo se ne može arhivirati' : 'Vozilo se ne može ukloniti'"
      :subtitle="selectedVehicleSubtitle"
      :message="state.selectedVehicleAction === 'archive'
        ? 'Ovo vozilo ima aktivne rezervacije i ne može se arhivirati. Uklonite ili premjestite rezervacije i zatim pokušajte ponovno arhivirati vozilo.'
        : 'Ovo vozilo je povezano s događajima, rezervacijama ili zapisnicima i ne može se ukloniti. Ako vozilo više nije u upotrebi, arhivirajte ga.'"
      primary-text="Razumijem"
      @close="clearSelectedVehicleForAction"
    />
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import AppConfirmDialog from '../../components/AppConfirmDialog.vue'
import Filters from '../../components/Filters.vue'
import Pagination from '../../components/Pagination.vue'
import ToolbarActionButton from '../../components/ToolbarActionButton.vue'
import { canAccessBrand } from '../../auth/authorization'
import { getEventBrandName, getVehicleBrandOptions, getPageCount } from '../../helpers'
import { apiClient } from '../../services/apiClient'
import { appStore, showRequestErrorToast, showSuccessToast } from '../../store/appStore'

const route = useRoute()
const router = useRouter()
let latestVehiclesRequestId = 0

const state = reactive({
  vehicleFilters: {
    search: null,
    brandCode: null,
    showArchivedVehicles: null,
  },
  currentPage: 1,
  itemsPerPage: 10,
  totalResults: 0,
  isLoadingVehicles: false,
  isSavingVehicleAction: false,
  selectedVehicleForAction: null,
  selectedVehicleAction: null,
  vehicleConfirmDialogOpen: false,
  occupiedVehicleDialogOpen: false,
  vehicles: [],
})

const pageTitle = computed(() => route.meta.title)
const selectedVehicleSubtitle = computed(() => state.selectedVehicleForAction
  ? `${state.selectedVehicleForAction.name} · ${state.selectedVehicleForAction.plate}`
  : '')

const vehicleFilterFields = [
  {
    key: 'search',
    type: 'search',
    placeholder: 'Pretraži po modelu, registraciji ili broju šasije...',
    width: '460px',
  },
  {
    key: 'brandCode',
    type: 'select',
    placeholder: 'MARKA',
    items: getVehicleBrandOptions().filter(({ value }) => canAccessBrand(value)),
    width: '230px',
  },
  {
    key: 'showArchivedVehicles',
    type: 'select',
    placeholder: 'PRIKAŽI',
    items: [
      { title: 'Arhivirana vozila', value: true },
    ],
    width: '230px',
  },
]

const vehicleTableHeaders = [
  { title: 'Marka', key: 'brandCode', sortable: false, width: '15%', headerProps: { class: 'text-mid' }, cellProps: { class: 'body-1-regular' } },
  { title: 'Tip vozila', key: 'name', sortable: false, width: '19%', headerProps: { class: 'text-mid' }, cellProps: { class: 'body-1-regular' } },
  { title: 'Registracijska oznaka', key: 'plate', sortable: false, width: '17%', headerProps: { class: 'text-mid' }, cellProps: { class: 'body-1-regular' } },
  { title: 'Broj šasije', key: 'vin', sortable: false, width: '24%', headerProps: { class: 'text-mid' }, cellProps: { class: 'body-1-regular' } },
  { title: 'Boja', key: 'color', sortable: false, width: '10%', headerProps: { class: 'text-mid' }, cellProps: { class: 'body-1-regular' } },
  { title: 'Status', key: 'status', sortable: false, align: 'end', width: '180px' },
  { title: '', key: 'actions', sortable: false, align: 'end', width: '5%' },
]

const vehicleStatusLabels = {
  active: 'Aktivno',
  occupied: 'Zauzeto',
  defective: 'Neispravno',
}

onMounted(loadVehiclesAsync)

watch(() => appStore.isOnline, () => loadVehiclesAsync(1))

function openVehicleDetail (_clickEvent, { item }) {
  router.push(createVehicleRoute('inventory-vehicle-detail', item))
}

function createVehicleRoute (routeName, vehicle) {
  return {
    name: routeName,
    params: { vehicleId: vehicle.id },
    state: { vehicle: { ...vehicle } },
  }
}

function getVehicleStatus (vehicle) {
  return !vehicle.isActive ? 'defective' : vehicle.isOccupied ? 'occupied' : 'active'
}

function openVehicleActionDialog (vehicle, vehicleAction) {
  state.selectedVehicleForAction = vehicle
  state.selectedVehicleAction = vehicleAction
  state.occupiedVehicleDialogOpen = vehicleAction === 'archive'
    ? vehicle.isOccupied
    : !vehicle.canVehicleBeDeleted
  state.vehicleConfirmDialogOpen = !state.occupiedVehicleDialogOpen
}

async function deleteSelectedVehicleAsync () {
  if (state.isSavingVehicleAction) {
    return
  }

  const vehicle = state.selectedVehicleForAction
  state.isSavingVehicleAction = true

  try {
    await apiClient.deleteAsync(`/api/vehicles/${vehicle.id}`)

    state.vehicleConfirmDialogOpen = false
    showSuccessToast('Vozilo je uspješno uklonjeno.')
    await loadVehiclesAsync(state.currentPage)
  } catch (error) {
    console.error('Failed to delete vehicle.', error)

    if (error.status === 409 && error.data?.error === 'vehicle_has_reservations') {
      state.vehicleConfirmDialogOpen = false
      state.occupiedVehicleDialogOpen = true
      await loadVehiclesAsync(state.currentPage)
    } else {
      showRequestErrorToast(error, 'Vozilo nije bilo moguće ukloniti.')
    }
  } finally {
    state.isSavingVehicleAction = false
  }
}

async function setVehicleArchivedAsync (vehicle, isArchived) {
  if (state.isSavingVehicleAction) {
    return
  }

  state.isSavingVehicleAction = true

  try {
    await apiClient.patchAsync(`/api/vehicles/${vehicle.id}`, { isArchived })

    state.vehicleConfirmDialogOpen = false
    showSuccessToast(isArchived
      ? 'Vozilo je uspješno arhivirano.'
      : 'Vozilo je uspješno vraćeno iz arhive.')
    await loadVehiclesAsync(state.currentPage)
  } catch (error) {
    console.error('Failed to set vehicle archived state.', error)

    if (isArchived && error.status === 409 && error.data?.error === 'vehicle_has_reservations') {
      state.vehicleConfirmDialogOpen = false
      state.occupiedVehicleDialogOpen = true
      await loadVehiclesAsync(state.currentPage)
    } else {
      showRequestErrorToast(error, isArchived
        ? 'Vozilo nije bilo moguće arhivirati.'
        : 'Vozilo nije bilo moguće vratiti iz arhive.')
    }
  } finally {
    state.isSavingVehicleAction = false
  }
}

function clearSelectedVehicleForAction () {
  if (state.vehicleConfirmDialogOpen || state.occupiedVehicleDialogOpen) {
    return
  }

  state.selectedVehicleForAction = null
  state.selectedVehicleAction = null
}

async function loadVehiclesAsync (page = 1) {
  const requestId = ++latestVehiclesRequestId
  state.isLoadingVehicles = true

  try {
    const { brandCode, search, showArchivedVehicles } = state.vehicleFilters

    const vehicleListResponse = await apiClient.getAsync('/api/vehicles', {
      queryParams: {
        brandCode,
        page,
        perPage: state.itemsPerPage,
        search: search?.trim() || null,
        showArchivedVehicles,
      },
    })
    if (requestId !== latestVehiclesRequestId) {
      return
    }

    const lastPage = getPageCount(
      vehicleListResponse.pagination.totalResults,
      vehicleListResponse.pagination.perPage
    )

    if (page > lastPage) {
      await loadVehiclesAsync(lastPage)
      return
    }

    state.vehicles = vehicleListResponse.results
    state.currentPage = vehicleListResponse.pagination.page
    state.itemsPerPage = vehicleListResponse.pagination.perPage
    state.totalResults = vehicleListResponse.pagination.totalResults
  } catch (error) {
    console.error('Failed to load vehicles.', error)

    if (requestId === latestVehiclesRequestId) {
      showRequestErrorToast(error, 'Vozila nije bilo moguće učitati.')
    }
  } finally {
    if (requestId === latestVehiclesRequestId) {
      state.isLoadingVehicles = false
    }
  }
}
</script>
